import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Briefcase, Users, Building2, Activity,
  ArrowUpRight, ArrowDownRight, AlertTriangle, MapPin, ArrowRight,
  TrendingUp, Plus, Search, Settings
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ass, setAss] = useState(null);
  const [condSt, setCondSt] = useState(null);

  useEffect(() => {
    api.get("/dashboard/operacional").then(r => setData(r.data));
    if (user?.role === "montadora") {
      api.get("/me/assinatura").then(r => setAss(r.data)).catch(() => {});
    }
    if (user?.role === "empresa") {
      api.get("/empresa/condicoes-status").then(r => setCondSt(r.data)).catch(() => {});
    }
  }, [user]);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-aj-text-muted text-sm">Carregando indicadores…</div>
      </div>
    );
  }

  const kpis = data.kpis || {};
  const cap = data.capacidade || { alertas: [], demanda_por_cidade: {}, por_cidade: {} };
  const totalAbertos = (kpis.montagens_abertas||0) + (kpis.revisoes_abertas||0) + (kpis.assistencias_abertas||0);
  const totalAtraso = kpis.juridico || 0;

  return (
    <div className="space-y-6">
      {user?.role === "admin" && <SaudeRede />}
      {user?.role === "empresa" && condSt?.pronto_para_publicar && !condSt?.publicada && (
        <div className="border-2 border-[#FF6700] bg-orange-50 p-4 flex flex-wrap items-center justify-between gap-3"
          data-testid="banner-publicar-condicoes">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#FF6700] mt-0.5 shrink-0" />
            <div>
              <div className="font-bold text-sm text-[#142766]">Suas condições ainda NÃO foram publicadas para a rede</div>
              <p className="text-xs text-aj-text-muted mt-0.5">
                Contrato assinado e documentação aprovada ✓ — mas Montadoras e Montadores só recebem as
                condições para aceite depois que você clicar em <strong>Publicar Condições</strong>.
              </p>
            </div>
          </div>
          <Link to="/admin/condicoes"
            className="btn-aj-primary text-sm whitespace-nowrap"
            data-testid="banner-publicar-cta">
            Publicar agora <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
      {/* Header com ações principais */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
            Painel Operacional
          </div>
          <h1 className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1">
            Dashboard
          </h1>
          <p className="text-sm text-aj-text-muted mt-1">
            Visão geral em tempo real da operação · {new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/importar" className="btn-aj-outline text-sm" data-testid="dash-acao-importar">
            <Plus className="w-4 h-4" /> Novo Serviço
          </Link>
          <Link to="/admin/pesquisar-montadoras" className="btn-aj-primary text-sm" data-testid="dash-acao-analisar">
            <MapPin className="w-4 h-4" /> Analisar Região
          </Link>
        </div>
      </div>

      {ass && <MontadoraAssinaturaBanner ass={ass} />}

      {/* KPI Cards principais — somente indicadores com fonte real */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard
          testid="kpi-servicos-abertos"
          icon={<Briefcase className="w-5 h-5" />}
          label="Serviços Abertos"
          value={totalAbertos}
          hint={`${totalAtraso} em atraso`}
          trend={totalAtraso > 0 ? `${totalAtraso} atraso` : null}
          warn={totalAtraso > 0}
          color="orange"
        />
        <KpiCard
          testid="kpi-montadores-ativos"
          icon={<Users className="w-5 h-5" />}
          label="Montadores Ativos"
          value={kpis.montadores_ativos || 0}
          hint="Rede ativa"
          color="blue"
        />
        <KpiCard
          testid="kpi-montadoras-parceiras"
          icon={<Building2 className="w-5 h-5" />}
          label="Montadoras Parceiras"
          value={kpis.montadoras_ativas ?? 0}
          hint="Status ativa"
          color="orange"
        />
      </div>

      {/* Coluna dupla: Alertas + Status */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Alertas (2/3) */}
        <div className="lg:col-span-2 aj-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
                Alertas
              </div>
              <h3 className="font-display font-bold text-lg text-aj-blue-deep mt-1">
                Capacidade Regional
              </h3>
            </div>
            <Link to="/admin/volumetria" className="aj-link-orange text-xs" data-testid="dash-alertas-ver-mais">
              Ver volumetria <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {cap.alertas.length === 0 ? (
            <div className="text-sm text-aj-text-muted py-8 text-center bg-aj-surface rounded-lg">
              <Activity className="w-8 h-8 mx-auto mb-2 text-aj-success" />
              Capacidade equilibrada em todas as regiões monitoradas.
            </div>
          ) : (
            <div className="space-y-2">
              {cap.alertas.slice(0, 6).map((a, i) => (
                <div key={i}
                  className={`p-3 rounded-lg border-l-4 flex items-center justify-between ${
                    a.tipo === "sem_tecnico"
                      ? "border-l-aj-danger bg-red-50"
                      : "border-l-aj-orange bg-aj-amber-soft/40"
                  }`}
                  data-testid={`alerta-${i}`}>
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${a.tipo === "sem_tecnico" ? "text-aj-danger" : "text-aj-orange"}`} />
                    <div>
                      <div className="font-bold text-sm text-aj-blue-deep">{a.cidade}</div>
                      <div className="text-xs text-aj-text-muted">
                        {a.tipo === "sem_tecnico" ? "Sem técnicos cadastrados" : "Excesso de demanda"}
                      </div>
                    </div>
                  </div>
                  <div className="font-mono text-xs text-aj-text-muted text-right">
                    <div>demanda {a.demanda}</div>
                    <div>cap {a.capacidade}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resumo Capacidade (1/3) */}
        <div className="aj-card p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
            Resumo
          </div>
          <h3 className="font-display font-bold text-lg text-aj-blue-deep mt-1 mb-4">
            Capacidade Geral
          </h3>
          <SmallStat label="Cidades com demanda" value={Object.keys(cap.demanda_por_cidade).length} />
          <SmallStat label="Cidades com cobertura" value={Object.keys(cap.por_cidade).length} />
          <SmallStat label="Alertas críticos" value={cap.alertas.filter(a => a.tipo === "sem_tecnico").length} danger />
          <SmallStat label="Em atendimento" value={kpis.em_atendimento || 0} />
          <SmallStat label="Aguardando cliente" value={kpis.aguardando_cliente || 0} />
          <SmallStat label="Finalizados" value={kpis.finalizados || 0} success />

          <Link to="/admin/insights" className="btn-aj-ghost-orange mt-5 w-full justify-center text-xs" data-testid="dash-resumo-insights">
            <TrendingUp className="w-3.5 h-3.5" /> Análise completa
          </Link>
        </div>
      </div>

      {/* Demanda × Capacidade por cidade */}
      {(Object.keys(cap.demanda_por_cidade).length > 0 || Object.keys(cap.por_cidade).length > 0) && (
        <div className="grid md:grid-cols-2 gap-5">
          <CidadeCard
            title="Demanda por cidade"
            data={cap.demanda_por_cidade}
            color="orange"
            empty="Nenhuma demanda registrada."
          />
          <CidadeCard
            title="Capacidade por cidade"
            data={cap.por_cidade}
            color="blue"
            suffix="/sem"
            empty="Cadastre montadores para liberar capacidade."
          />
        </div>
      )}

      <RankingRegioes />
    </div>
  );
}

function RankingRegioes() {
  const [dados, setDados] = useState(null);
  const [dias, setDias] = useState(90);
  useEffect(() => {
    api.get(`/empresa/ranking-regioes?dias=${dias}`).then(r => setDados(r.data)).catch(() => {});
  }, [dias]);
  if (!dados || dados.total_servicos === 0) return null;
  const Col = ({ titulo, itens, testid }) => {
    const max = Math.max(...itens.map(([, v]) => v), 1);
    return (
      <div data-testid={testid}>
        <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted mb-2">{titulo}</div>
        <div className="space-y-1.5">
          {itens.slice(0, 8).map(([nome, qtd], i) => (
            <div key={nome} className="flex items-center gap-2 text-xs">
              <span className="w-4 text-right font-mono text-aj-text-muted">{i + 1}º</span>
              <div className="flex-1">
                <div className="flex justify-between">
                  <span className="font-semibold text-aj-blue-deep truncate max-w-[70%]">{nome}</span>
                  <span className="font-mono font-bold">{qtd}</span>
                </div>
                <div className="h-1.5 bg-[#F1F5F9] rounded mt-0.5">
                  <div className="h-full rounded bg-[#FF6700]" style={{ width: `${(qtd / max) * 100}%` }} />
                </div>
              </div>
            </div>
          ))}
          {itens.length === 0 && <div className="text-[11px] text-aj-text-muted">Sem dados no período.</div>}
        </div>
      </div>
    );
  };
  return (
    <div className="aj-card p-6" data-testid="dash-ranking-regioes">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="font-display font-bold text-aj-blue-deep">Ranking de Regiões que mais vendem</div>
          <div className="text-[11px] text-aj-text-muted">
            {dados.total_servicos} serviço(s) nos últimos {dados.dias} dias · use para campanhas e prospecção de montadores
          </div>
        </div>
        <select value={dias} onChange={e => setDias(Number(e.target.value))}
          className="border border-aj-border bg-white text-xs p-1.5 rounded" data-testid="ranking-periodo">
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
          <option value={180}>Últimos 180 dias</option>
          <option value={365}>Último ano</option>
        </select>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Col titulo="Cidades" itens={dados.cidades} testid="ranking-cidades" />
        <Col titulo="Bairros" itens={dados.bairros} testid="ranking-bairros" />
      </div>
    </div>
  );
}

function KpiCard({ testid, icon, label, value, hint, trend, warn, color = "orange" }) {
  return (
    <div className="aj-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className={color === "blue" ? "aj-icon-tile-blue w-10 h-10" : "aj-icon-tile w-10 h-10"} style={{ width: 40, height: 40 }}>
          {icon}
        </div>
        {trend && (
          <div className={`text-[10px] font-bold flex items-center gap-0.5 ${warn ? "text-aj-danger" : "text-aj-success"}`}>
            {warn ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
            {trend}
          </div>
        )}
      </div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted leading-tight">{label}</div>
      <div className="font-display font-extrabold text-2xl text-aj-blue-deep mt-1">{value}</div>
      {hint && <div className="text-[11px] text-aj-text-muted mt-0.5">{hint}</div>}
    </div>
  );
}

function SmallStat({ label, value, danger, success }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-aj-border last:border-0">
      <span className="text-xs text-aj-text-muted">{label}</span>
      <span className={`font-display font-bold text-sm ${danger ? "text-aj-danger" : success ? "text-aj-success" : "text-aj-blue-deep"}`}>
        {value}
      </span>
    </div>
  );
}

function CidadeCard({ title, data, color, suffix = "", empty }) {
  const entries = Object.entries(data);
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="aj-card p-6">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
        {title}
      </div>
      {entries.length === 0 ? (
        <div className="text-sm text-aj-text-muted mt-4">{empty}</div>
      ) : (
        <div className="space-y-2 mt-4">
          {entries.slice(0, 8).map(([cidade, n]) => (
            <div key={cidade}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium text-aj-text">{cidade}</span>
                <span className="font-mono font-bold text-aj-blue-deep">{n}{suffix}</span>
              </div>
              <div className="h-1.5 bg-aj-surface rounded overflow-hidden">
                <div
                  className={`h-full rounded ${color === "orange" ? "bg-gradient-to-r from-aj-orange to-aj-amber" : "bg-gradient-to-r from-aj-blue-dark to-aj-blue"}`}
                  style={{ width: `${(n / max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MontadoraAssinaturaBanner({ ass }) {
  if (!ass || !ass.status_assinatura || ass.status_assinatura === "n/a") return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";
  const palette = {
    aguardando_aprovacao: { border: "border-aj-orange", bg: "bg-aj-amber-soft" },
    gratuito: { border: "border-aj-success", bg: "bg-green-50" },
    ativo: { border: "border-aj-blue", bg: "bg-blue-50" },
    pendente: { border: "border-aj-orange", bg: "bg-aj-amber-soft" },
    suspenso: { border: "border-aj-danger", bg: "bg-red-50" },
    cancelado: { border: "border-aj-border-strong", bg: "bg-aj-surface" },
  }[ass.status_assinatura] || { border: "border-aj-border", bg: "bg-aj-surface" };

  const messages = {
    aguardando_aprovacao: "Cadastro aguardando aprovação do admin. Após aprovação, 6 meses de plataforma gratuita.",
    gratuito: `Plataforma gratuita até ${fmt(ass.data_fim_gratuidade)}. Após essa data, mensalidade por faixa.`,
    ativo: `Assinatura ativa · Faixa: ${ass.faixa_atual || "—"} · Valor projetado R$ ${Number(ass.valor_projetado || 0).toFixed(2)}`,
    pendente: `Mensalidade pendente. Próx. cobrança: ${fmt(ass.proxima_cobranca)}.`,
    suspenso: "Conta suspensa. Regularize para reativar.",
    cancelado: "Assinatura cancelada.",
  };

  return (
    <div className={`rounded-lg border-l-4 ${palette.border} ${palette.bg} p-4`} data-testid={`mont-banner-${ass.status_assinatura}`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="font-display font-bold text-sm text-aj-blue-deep capitalize">
            Assinatura: {ass.status_assinatura.replace("_", " ")}
          </div>
          <div className="text-xs text-aj-text-muted mt-1">{messages[ass.status_assinatura]}</div>
        </div>
        {ass.atendimentos_mes_corrente !== undefined && (
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Mês atual</div>
            <div className="font-display font-extrabold text-2xl text-aj-blue-deep">{ass.atendimentos_mes_corrente}</div>
            <div className="text-[10px] text-aj-text-muted">atendimentos</div>
          </div>
        )}
      </div>
    </div>
  );
}

function SaudeRede() {
  const [dados, setDados] = useState(null);
  const [aberto, setAberto] = useState(null);
  useEffect(() => {
    api.get("/admin/saude-rede").then(r => setDados(r.data)).catch(() => setDados([]));
  }, []);
  if (!dados || dados.length === 0) return null;
  const fracasTotal = dados.reduce((a, e) => a + e.regioes_fracas, 0);
  return (
    <div className="aj-card p-5" data-testid="saude-rede">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Rede · Aceites Comerciais</div>
          <h2 className="font-display font-extrabold text-xl text-aj-blue-deep mt-0.5">Saúde da Rede por Região</h2>
        </div>
        {fracasTotal > 0 && (
          <span className="px-3 py-1 rounded-md bg-red-100 text-[#B91C1C] text-xs font-bold uppercase" data-testid="saude-fracas">
            {fracasTotal} região(ões) sem nenhum aceite
          </span>
        )}
      </div>
      <div className="mt-4 space-y-2">
        {dados.map(e => (
          <div key={e.empresa_id} className="border border-aj-border rounded-lg" data-testid={`saude-emp-${e.empresa_id}`}>
            <button onClick={() => setAberto(aberto === e.empresa_id ? null : e.empresa_id)}
              className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-aj-surface transition"
              data-testid={`saude-toggle-${e.empresa_id}`}>
              <span className="font-semibold text-sm text-aj-blue-deep truncate">{e.empresa}</span>
              <span className="flex gap-3 text-xs shrink-0">
                <span className="text-emerald-700 font-bold">{e.total_aceitos} aceitos</span>
                <span className="text-amber-600 font-bold">{e.total_pendentes} pendentes</span>
                <span className="text-red-500 font-bold">{e.total_recusados} recusaram</span>
                {e.regioes_fracas > 0 && <span className="px-1.5 rounded bg-red-100 text-[#B91C1C] font-bold">{e.regioes_fracas} fraca(s)</span>}
              </span>
            </button>
            {aberto === e.empresa_id && (
              <div className="px-3 pb-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {e.regioes.map(r => (
                  <div key={r.cidade} className={`text-xs p-2 rounded border ${r.fraca ? "border-red-300 bg-red-50" : "border-aj-border bg-aj-surface/50"}`}
                    data-testid={`saude-cidade-${r.cidade}`}>
                    <div className="font-bold text-aj-blue-deep">{r.cidade} {r.fraca && <span className="text-[#B91C1C]">⚠ fraca</span>}</div>
                    <div className="text-aj-text-muted">{r.aceitos} aceito(s) · {r.pendentes} pendente(s) · {r.recusados} recusado(s)</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

